import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { chatService, ChatMessage, ChatConversation } from '../services/ChatService';
import { supabase } from '../supabaseClient';
import { useNotificationService } from '../services/NotificationService';

const isDev = process.env.NODE_ENV !== 'production';

export interface Message {
  id: string;
  senderId: string;
  senderName: string;
  senderType: 'soy_iyire' | 'busco_iyire';
  content: string;
  type: 'text' | 'image' | 'location' | 'file';
  timestamp: Date;
  read: boolean;
  conversationId: string;
  metadata?: any;
}

export interface Conversation {
  id: string;
  participant1Id: string;
  participant1Name: string;
  participant1Apellido?: string;
  participant1Type: 'soy_iyire' | 'busco_iyire';
  participant2Id: string;
  participant2Name: string;
  participant2Apellido?: string;
  participant2Type: 'soy_iyire' | 'busco_iyire';
  jobId?: string;
  jobTitle?: string;
  lastMessage?: Message;
  unreadCount: number;
  createdAt: Date;
  updatedAt: Date;
}

interface ChatState {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Message[];
  showChatList: boolean;
  showChat: boolean;
  loading: boolean;
  currentUser: any;
  mutedConversations: string[];
}

interface ChatContextType extends ChatState {
  // Acciones
  loadConversations: () => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, content: string, type?: 'text' | 'image' | 'location' | 'file', metadata?: any) => Promise<void>;
  createConversation: (participant2Id: string, participant2Name: string, participant2Type: 'soy_iyire' | 'busco_iyire', jobId?: string, jobTitle?: string) => Promise<string | null>;
  setActiveConversation: (conversation: Conversation | null) => void;
  markAsRead: (conversationId: string) => Promise<void>;
  toggleChatList: () => void;
  toggleChat: () => void;
  setShowChatList: (show: boolean) => void;
  setShowChat: (show: boolean) => void;
  // Nuevas acciones del menú de chat
  markAsUnread: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  toggleMute: (conversationId: string) => void;
  isMuted: (conversationId: string) => boolean;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const defaultChatContext: ChatContextType = {
  conversations: [],
  activeConversation: null,
  messages: [],
  showChatList: false,
  showChat: false,
  loading: false,
  currentUser: null,
  mutedConversations: [],
  loadConversations: async () => {},
  loadMessages: async () => {},
  sendMessage: async () => {},
  createConversation: async () => null,
  setActiveConversation: () => {},
  markAsRead: async () => {},
  toggleChatList: () => {},
  toggleChat: () => {},
  setShowChatList: () => {},
  setShowChat: () => {},
  markAsUnread: () => {},
  deleteConversation: async () => {},
  toggleMute: () => {},
  isMuted: () => false,
};

// Función para convertir mensaje del servicio al formato del contexto
const convertServiceMessage = (msg: ChatMessage): Message => ({
  id: msg.id,
  senderId: msg.sender_id,
  senderName: msg.sender_name,
  senderType: msg.sender_type,
  content: msg.content,
  type: msg.message_type as 'text' | 'image' | 'location' | 'file',
  timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
  read: msg.read,
  conversationId: msg.conversation_id,
  metadata: msg.metadata
});

// Función para convertir conversación del servicio al formato del contexto
const convertServiceConversation = (conv: ChatConversation): Conversation => {
  // Extraer nombre y apellido de participant1
  const participant1Parts = conv.participant1_name.split(' ');
  const participant1Name = participant1Parts[0] || conv.participant1_name;
  const participant1Apellido = participant1Parts.length > 1 ? participant1Parts.slice(1).join(' ') : '';

  // Extraer nombre y apellido de participant2
  const participant2Parts = conv.participant2_name.split(' ');
  const participant2Name = participant2Parts[0] || conv.participant2_name;
  const participant2Apellido = participant2Parts.length > 1 ? participant2Parts.slice(1).join(' ') : '';

  return {
    id: conv.id,
    participant1Id: conv.participant1_id,
    participant1Name: participant1Name,
    participant1Apellido: participant1Apellido,
    participant1Type: conv.participant1_type,
    participant2Id: conv.participant2_id,
    participant2Name: participant2Name,
    participant2Apellido: participant2Apellido,
    participant2Type: conv.participant2_type,
    jobId: conv.job_id,
    jobTitle: conv.job_title,
    lastMessage: Array.isArray((conv as any).last_message) && (conv as any).last_message.length > 0
      ? convertServiceMessage((conv as any).last_message[0])
      : (conv as any).last_message && !(Array.isArray((conv as any).last_message))
        ? convertServiceMessage((conv as any).last_message as any)
        : undefined,
    unreadCount: 0, // Se calculará dinámicamente
    createdAt: conv.created_at ? new Date(conv.created_at) : new Date(),
    updatedAt: conv.updated_at ? new Date(conv.updated_at) : new Date()
  };
};

export const RealChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ChatState>(() => {
    let muted: string[] = [];
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const raw = window.localStorage.getItem('muted_conversations');
        muted = raw ? JSON.parse(raw) : [];
      }
    } catch {
      muted = [];
    }
    return {
      conversations: [],
      activeConversation: null,
      messages: [],
      showChatList: false,
      showChat: false,
      loading: false,
      currentUser: null,
      mutedConversations: muted,
    };
  });

  const notificationService = useNotificationService();

  // Cargar conversaciones
  const loadConversations = useCallback(async () => {
    if (!state.currentUser) return;

    setState(prev => ({ ...prev, loading: true }));
    try {
      const conversations = await chatService.getConversations(state.currentUser.id);

      // Convertir conversaciones y asignar unreadCount usando los campos de la tabla
      const convertedConversations = conversations.map(conv => {
        const base = convertServiceConversation(conv as any);
        const isParticipant1 = conv.participant1_id === state.currentUser!.id;
        const unreadCount = isParticipant1
          ? conv.unread_count_participant1
          : conv.unread_count_participant2;

        return {
          ...base,
          unreadCount: unreadCount || 0,
        };
      });

      // Enriquecer con nombre y apellido reales desde la tabla usuarios
      const participantIds = Array.from(new Set(
        convertedConversations.flatMap(c => [c.participant1Id, c.participant2Id])
      ));

      let enrichedConversations = convertedConversations;

      if (participantIds.length > 0) {
        const { data: usuariosData, error: usuariosError } = await supabase
          .from('usuarios')
          .select('id, nombre, apellido')
          .in('id', participantIds as string[]);

        if (!usuariosError && usuariosData) {
          const idToUser: Record<string, { nombre?: string; apellido?: string }> = {};
          usuariosData.forEach((u: any) => {
            idToUser[u.id] = { nombre: u.nombre, apellido: u.apellido };
          });

          enrichedConversations = convertedConversations.map(conv => {
            const u1 = idToUser[conv.participant1Id] || {};
            const u2 = idToUser[conv.participant2Id] || {};
            return {
              ...conv,
              participant1Name: u1.nombre || conv.participant1Name,
              participant1Apellido: u1.apellido || conv.participant1Apellido,
              participant2Name: u2.nombre || conv.participant2Name,
              participant2Apellido: u2.apellido || conv.participant2Apellido,
            };
          });
        }
      }

      setState(prev => ({ ...prev, conversations: enrichedConversations, loading: false }));
    } catch (error) {
      console.error('Error loading conversations:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [state.currentUser]);

  // Cargar usuario actual y escuchar cambios de sesión (solo una vez)
  useEffect(() => {
    const loadCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setState(prev => ({ ...prev, currentUser: session.user }));
      }
    };

    loadCurrentUser();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setState(prev => ({ ...prev, currentUser: session.user }));
      } else {
        setState(prev => ({ 
          ...prev, 
          currentUser: null, 
          conversations: [], 
          messages: [], 
          activeConversation: null 
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Marcar como no leída (local)
  const markAsUnread = (conversationId: string) => {
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(c =>
        c.id === conversationId ? { ...c, unreadCount: Math.max(c.unreadCount || 0, 1) } : c
      )
    }));
  };

  // Cargar mensajes de una conversación
  const loadMessages = useCallback(async (conversationId: string) => {
    try {
      const messages = await chatService.getMessages(conversationId);
      const convertedMessages = messages.map(convertServiceMessage);
      setState(prev => ({ ...prev, messages: convertedMessages }));
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, []);

  // Enviar mensaje
  const sendMessage = async (
    conversationId: string, 
    content: string, 
    type: 'text' | 'image' | 'location' | 'file' = 'text',
    metadata?: any
  ) => {
    if (isDev) {
      console.log('🚀 SEND MESSAGE - Iniciando envío:', { conversationId, content, currentUser: state.currentUser });
    }
    if (!state.currentUser) {
      console.error('❌ No hay usuario actual');
      return;
    }

    const currentUserType = getUserType();
    if (isDev) {
      console.log('👤 Tipo de usuario:', currentUserType);
    }

    try {
      // Obtener nombre y apellido del usuario actual desde la BD
      const { data: usuarioDB } = await supabase
        .from('usuarios')
        .select('nombre, apellido')
        .eq('email', state.currentUser.email)
        .single();

      const nombre = usuarioDB?.nombre || state.currentUser.user_metadata?.name || state.currentUser.email?.split('@')[0] || 'Usuario';
      const apellido = usuarioDB?.apellido || '';
      const senderName = apellido ? `${nombre} ${apellido}` : nombre;
      
      console.log('📝 Nombre del remitente:', senderName);

      const message = await chatService.sendMessage(
        conversationId,
        state.currentUser.id,
        senderName,
        currentUserType,
        content,
        type,
        metadata
      );

      if (isDev) {
        console.log('📤 Mensaje enviado al servicio:', message);
      }

      if (message) {
        const convertedMessage = convertServiceMessage(message);
        if (isDev) {
          console.log('🔄 Mensaje convertido:', convertedMessage);
        }
        
        setState(prev => ({
          ...prev,
          messages: [...prev.messages, convertedMessage]
        }));

        if (isDev) {
          console.log('✅ Mensaje agregado al estado local');
        }

        // No generamos notificación local para el emisor
        // El receptor recibirá su notificación vía suscripción en tiempo real
      } else {
        console.error('❌ No se pudo enviar el mensaje - respuesta vacía');
      }
    } catch (error) {
      console.error('❌ Error sending message:', error);
    }
  };

  // Determinar tipo de usuario basado en la URL o metadata
  const getUserType = useCallback((): 'soy_iyire' | 'busco_iyire' => {
    const path = window.location.pathname;
    if (path.includes('/soy-niva/')) {
      return 'soy_iyire';
    } else if (path.includes('/busco-niva/')) {
      return 'busco_iyire';
    }
    // Fallback: intentar determinar por metadata del usuario
    const userType = state.currentUser?.user_metadata?.user_type;
    if (userType === 'soy_iyire' || userType === 'busco_iyire') {
      return userType;
    }
    // Default fallback
    return 'busco_iyire';
  }, [state.currentUser]);

  // Crear nueva conversación
  const createConversation = async (
    participant2Id: string,
    participant2Name: string,
    participant2Type: 'soy_iyire' | 'busco_iyire',
    jobId?: string,
    jobTitle?: string
  ): Promise<string | null> => {
    if (!state.currentUser) return null;

    const currentUserType = getUserType();

    try {
      // Obtener nombre y apellido del usuario actual desde la BD
      const { data: usuarioDB } = await supabase
        .from('usuarios')
        .select('nombre, apellido')
        .eq('email', state.currentUser.email)
        .single();

      const nombre = usuarioDB?.nombre || state.currentUser.user_metadata?.name || state.currentUser.email?.split('@')[0] || 'Usuario';
      const apellido = usuarioDB?.apellido || '';
      const currentUserName = apellido ? `${nombre} ${apellido}` : nombre;
      
      console.log('📝 Nombre del usuario actual:', currentUserName);

      const conversation = await chatService.createConversation(
        state.currentUser.id,
        currentUserName,
        currentUserType,
        participant2Id,
        participant2Name,
        participant2Type,
        jobId,
        jobTitle
      );

      if (conversation) {
        const convertedConversation = convertServiceConversation(conversation);
        setState(prev => ({
          ...prev,
          conversations: [convertedConversation, ...prev.conversations]
        }));
        return conversation.id;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }

    return null;
  };

  // Establecer conversación activa
  const setActiveConversation = (conversation: Conversation | null) => {
    console.log('🎯 Estableciendo conversación activa:', conversation?.id);
    setState(prev => ({ ...prev, activeConversation: conversation }));
    if (conversation) {
      loadMessages(conversation.id);
      markAsRead(conversation.id);
      // Reiniciar contador de no leídos localmente al abrir
      setState(prev => ({
        ...prev,
        conversations: prev.conversations.map(c =>
          c.id === conversation.id ? { ...c, unreadCount: 0 } : c
        )
      }));
    }
  };

  // Marcar como leído
  const markAsRead = async (conversationId: string) => {
    if (!state.currentUser) return;

    try {
      await chatService.markMessagesAsRead(conversationId, state.currentUser.id);
      setState(prev => ({
        ...prev,
        conversations: prev.conversations.map(conv =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      }));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Silenciar / des-silenciar conversación
  const toggleMute = (conversationId: string) => {
    setState(prev => {
      const set = new Set(prev.mutedConversations);
      if (set.has(conversationId)) {
        set.delete(conversationId);
      } else {
        set.add(conversationId);
      }
      const list = Array.from(set);
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('muted_conversations', JSON.stringify(list));
        }
      } catch {
        // ignorar errores de almacenamiento en navegadores restringidos
      }
      return { ...prev, mutedConversations: list };
    });
  };

  const isMuted = useCallback((conversationId: string) => state.mutedConversations.includes(conversationId), [state.mutedConversations]);

  // Borrar conversación
  const deleteConversation = async (conversationId: string) => {
    try {
      await chatService.deleteConversation(conversationId);
      setState(prev => ({
        ...prev,
        conversations: prev.conversations.filter(c => c.id !== conversationId),
        activeConversation: prev.activeConversation?.id === conversationId ? null : prev.activeConversation,
        messages: prev.activeConversation?.id === conversationId ? [] : prev.messages,
        showChat: prev.activeConversation?.id === conversationId ? false : prev.showChat
      }));
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Toggle chat list
  const toggleChatList = () => {
    setState(prev => ({ ...prev, showChatList: !prev.showChatList }));
  };

  // Toggle chat
  const toggleChat = () => {
    setState(prev => ({ ...prev, showChat: !prev.showChat }));
  };

  // Set show chat list
  const setShowChatList = (show: boolean) => {
    setState(prev => ({ ...prev, showChatList: show }));
    // Cargar conversaciones cuando se abre la lista
    if (show && state.currentUser) {
      console.log('🔄 Abriendo lista de chats, cargando conversaciones...');
      loadConversations();
    }
  };

  // Set show chat
  const setShowChat = (show: boolean) => {
    setState(prev => ({ ...prev, showChat: show }));
  };

  // Fallback: Cargar mensajes periódicamente si la suscripción no funciona (solo en desarrollo)
  useEffect(() => {
    if (!isDev) return; // evitar cualquier polling en producción
    if (!state.currentUser || !state.activeConversation) return;

    const interval = setInterval(async () => {
      console.log('🔄 Fallback DEV: Recargando mensajes...');
      await loadMessages(state.activeConversation!.id);
    }, 8000); // un poco más espaciado en dev

    return () => clearInterval(interval);
  }, [state.currentUser, state.activeConversation, loadMessages]);

  // Suscripción en tiempo real para mensajes nuevos
  useEffect(() => {
    const currentUserId = state.currentUser?.id;

    if (!currentUserId) {
      if (isDev) {
        console.log('❌ No hay usuario actual para suscripción');
      }
      return;
    }

    if (isDev) {
      console.log('🔔 Iniciando suscripción en tiempo real para usuario:', currentUserId);
    }

    const subscription = supabase
      .channel('chat_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const newMessage = payload.new as ChatMessage;

          let shouldNotify = false;
          let notifyTitle = '';
          let notifyBody = '';

          setState(prev => {
            // No procesar si no hay usuario actual o es mensaje propio
            if (!prev.currentUser || newMessage.sender_id === prev.currentUser.id) {
              return prev;
            }

            const isActiveConv = prev.activeConversation && newMessage.conversation_id === prev.activeConversation.id;
            const convertedMessage = convertServiceMessage(newMessage);

            const nextState: typeof prev = {
              ...prev,
              messages: isActiveConv ? [...prev.messages, convertedMessage] : prev.messages,
              conversations: !isActiveConv
                ? prev.conversations.map(c =>
                    c.id === newMessage.conversation_id
                      ? { ...c, unreadCount: (c.unreadCount || 0) + 1 }
                      : c
                  )
                : prev.conversations,
            };

            const isMutedLocal = prev.mutedConversations.includes(newMessage.conversation_id);
            if (!isMutedLocal) {
              shouldNotify = true;
              notifyTitle = newMessage.sender_name;
              notifyBody = newMessage.content;
            }

            return nextState;
          });

          if (shouldNotify) {
            notificationService.notifyNewMessage(notifyTitle, notifyBody);
          }
        }
      )
      .subscribe((status) => {
        if (isDev) {
          if (status === 'SUBSCRIBED') {
            console.log('✅ Suscripción de chat activa');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Error en la suscripción de chat');
          } else if (status === 'TIMED_OUT') {
            console.error('⏰ Suscripción de chat expirada');
          } else if (status === 'CLOSED') {
            console.log('🔌 Suscripción de chat cerrada');
          }
        }
      });

    return () => {
      if (isDev) {
        console.log('🔌 Desconectando suscripción de chat');
      }
      subscription.unsubscribe();
    };
  }, [state.currentUser?.id, notificationService]);

  const value: ChatContextType = {
    ...state,
    loadConversations,
    loadMessages,
    sendMessage,
    createConversation,
    setActiveConversation,
    markAsRead,
    markAsUnread,
    deleteConversation,
    toggleMute,
    isMuted,
    toggleChatList,
    toggleChat,
    setShowChatList,
    setShowChat
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useRealChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('useRealChat usado fuera de RealChatProvider; devolviendo contexto vacío');
    }
    return defaultChatContext;
  }
  return context;
};
